import { useCallback } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import TreeViewBase, { TreeViewProps } from '@mui/lab/TreeView';
import { Skeleton } from '@mui/material';
import {
  type Totals,
  type TreeLeaf,
  type TreeTotalsNode,
  type TreeTotalsType,
} from '../../types';
import { Flex, FlexColumn } from '../Flex';
import TreeItem from './TreeItem';

export interface TreeViewNode {
  id: string;
  name: string;
  parentId?: string;
  totals?: Totals;
  children?: TreeViewNode[];
  nodeLevel: number;
  resourceType: TreeLeaf['resource_type'] | 'folder';
  totalsType?: TreeTotalsType;
  totalsRequestKey?: string;
}

const TreeViewStyled = styled(TreeViewBase)`
  .MuiTreeItem-group {
    margin-left: 3px;
  }
`;

const LoadingStateContainer = styled(FlexColumn)`
  padding: 8px 16px;
  gap: 8px;
`;

const TreeItemSkeleton = styled(Flex)<{ indent: number }>`
  align-items: center;
  gap: 8px;
  padding-left: ${(props) => props.indent * 24}px;
`;

type BaseProps = {
  selected?: { id?: string; parentId?: string } | undefined;
  searchQuery?: string;
  onNodeExpandCollapse?: Function;
  renderNodeIcon?: (data: TreeViewNode) => JSX.Element;
  showFolderIcon?: boolean;
  renderFolderIcon?: (data: TreeViewNode) => JSX.Element | undefined;
  nodeButton?: JSX.Element;
  onNodeButtonClick?: Function;
  selectionColor?: string;
  boldLabels?: boolean;
  disabledNodes?: string[];
  allowTopLevelFolderSelection?: boolean;
  showTotals?: boolean;
  renderTotals?: (node: TreeTotalsNode) => ReactNode;
};

type LoadingProps = TreeViewProps &
  BaseProps & {
    isLoading: true;
    data?: TreeViewNode;
    onNodeClick?: Function;
  };

type LoadedProps = TreeViewProps &
  BaseProps & {
    isLoading?: false;
    data: TreeViewNode;
    onNodeClick: Function;
  };

type Props = LoadingProps | LoadedProps;

const renderTree = (
  nodes: TreeViewNode,
  selected: { id?: string; parentId?: string } | undefined,
  onNodeClick: Function,
  searchQuery?: string,
  onNodeExpandCollapse?: Function,
  renderNodeIcon?: (data: TreeViewNode) => JSX.Element,
  showFolderIcon?: boolean,
  renderFolderIcon?: (data: TreeViewNode) => JSX.Element | undefined,
  nodeButton?: JSX.Element,
  onNodeButtonClick?: Function,
  selectionColor?: string,
  boldLabels?: boolean,
  disabledNodes?: string[],
  expanded?: string[],
  showTotals: boolean = true,
  renderTotals?: (node: TreeTotalsNode) => ReactNode,
) => (
  <TreeItem
    aria-label="tree-item"
    key={nodes.id}
    nodeId={nodes.id}
    parentId={nodes.parentId}
    label={nodes.name}
    totals={nodes.totals}
    totalsType={nodes.totalsType}
    totalsRequestKey={nodes.totalsRequestKey}
    searchQuery={searchQuery}
    isSelected={
      selected?.id === nodes.id &&
      (selected?.parentId ? selected?.parentId === nodes.parentId : true)
    }
    nodeIcon={renderNodeIcon?.(nodes)}
    showFolderIcon={showFolderIcon}
    folderIcon={renderFolderIcon?.(nodes)}
    nodeButton={nodeButton}
    onNodeButtonClick={onNodeButtonClick}
    selectionColor={selectionColor}
    boldLabels={boldLabels}
    disabled={disabledNodes?.includes(nodes.id)}
    onExpandCollapse={onNodeExpandCollapse}
    onNodeClick={onNodeClick}
    showTotals={showTotals}
    renderTotals={renderTotals}
  >
    {Array.isArray(nodes.children)
      ? nodes.children.map((node) =>
          renderTree(
            node,
            selected,
            onNodeClick,
            searchQuery,
            onNodeExpandCollapse,
            renderNodeIcon,
            showFolderIcon,
            renderFolderIcon,
            nodeButton,
            onNodeButtonClick,
            selectionColor,
            boldLabels,
            disabledNodes,
            expanded,
            showTotals,
            renderTotals,
          ),
        )
      : null}
  </TreeItem>
);

const TREE_SKELETON_ITEMS = [
  { indent: 0, width: 180 },
  { indent: 1, width: 140 },
  { indent: 1, width: 140 },
  { indent: 0, width: 170 },
  { indent: 1, width: 150 },
  { indent: 2, width: 130 },
  { indent: 0, width: 170 },
  { indent: 1, width: 160 },
  { indent: 2, width: 130 },
  { indent: 2, width: 130 },
];

const TreeViewLoadingState = () => (
  <LoadingStateContainer>
    {TREE_SKELETON_ITEMS.map((item, index) => (
      <TreeItemSkeleton key={index} indent={item.indent}>
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={item.width} height={24} />
      </TreeItemSkeleton>
    ))}
  </LoadingStateContainer>
);

const TreeView = ({
  data,
  selected,
  searchQuery,
  onNodeClick,
  onNodeExpandCollapse,
  renderNodeIcon,
  showFolderIcon,
  renderFolderIcon,
  nodeButton,
  onNodeButtonClick,
  selectionColor,
  boldLabels,
  disabledNodes,
  expanded,
  showTotals = true,
  renderTotals,
  isLoading,
  ...props
}: Props) => {
  const handleNodeExpandCollapse = useCallback(
    (id: string) => {
      if (expanded?.includes(id)) {
        onNodeExpandCollapse?.(
          expanded.filter((expandedId) => expandedId !== id),
        );
      } else {
        onNodeExpandCollapse?.([...(expanded || []), id]);
      }
    },
    [expanded, onNodeExpandCollapse],
  );

  if (isLoading) {
    return <TreeViewLoadingState />;
  }

  return (
    // @ts-ignore
    <TreeViewStyled
      {...props}
      expanded={expanded}
      selected={selected?.id || ''}
    >
      {renderTree(
        data,
        selected,
        onNodeClick,
        searchQuery,
        handleNodeExpandCollapse,
        renderNodeIcon,
        showFolderIcon,
        renderFolderIcon,
        nodeButton,
        onNodeButtonClick,
        selectionColor,
        boldLabels,
        disabledNodes,
        expanded,
        showTotals,
        renderTotals,
      )}
    </TreeViewStyled>
  );
};

export default TreeView;
